import api from './api';

export interface ChatConversation {
  id: string;
  type: 'direct' | 'group';
  name: string;
  lastActivityAt?: string;
  unreadCount?: number;
  otherUserId?: string | null;
}

export interface ChatMessageDTO {
  id: string;
  conversation_id: string;
  sender_user_id: string;
  content: string;
  created_at: string;
  sender?: { displayName?: string; email?: string; avatarUrl?: string | null };
}

export interface ChatMessage {
  id: string;
  text: string;
  createTime?: string;
  sender?: { displayName?: string; email?: string; avatarUrl?: string | null };
  senderId?: string | null;
  imageUrl?: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  reactions?: { up: number; down: number; smile: number; heart: number; haha: number; exclamation: number; question: number };
  myReaction?: 'up'|'down'|'smile'|'heart'|'haha'|'exclamation'|'question'|null;
}

export interface ListMessagesResult { items: ChatMessage[]; nextPageToken: string | null; readUpToByOthers?: string | null }

function getInternalChatApiBase(): string {
  const base = (api as any).defaults?.baseURL || 'http://localhost:5000/api';
  return base.replace(/\/?$/, '');
}

/** Jedna ścieżka mapowania wiersza z GET/POST internal-chat → model UI (żeby POST gemma-message i lista były spójne). */
function mapApiItemToChatMessage(m: any): ChatMessage {
  const apiBase = getInternalChatApiBase();
  const isUpload = typeof m.content === 'string' && m.content.startsWith('/uploads/');
  const msgType = m.msg_type || (isUpload ? 'image' : null);
  const isImage = msgType === 'image' || (isUpload && !msgType);
  const isFile = msgType === 'file';
  return {
    id: String(m.id),
    text: (isImage || isFile) ? '' : (m.content || ''),
    imageUrl: isImage ? (apiBase + String(m.content)) : undefined,
    fileUrl: isFile ? (apiBase + String(m.content)) : undefined,
    fileName: m.original_filename || undefined,
    fileSize: m.file_size ? Number(m.file_size) : undefined,
    createTime: m.created_at,
    sender: m.sender,
    senderId: m.sender_user_id,
    reactions: {
      up: Number(m.reactions?.up ?? m.reactions_up ?? 0),
      down: Number(m.reactions?.down ?? m.reactions_down ?? 0),
      smile: Number(m.reactions?.smile ?? m.reactions_smile ?? 0),
      heart: Number(m.reactions?.heart ?? m.reactions_heart ?? 0),
      haha: Number(m.reactions?.haha ?? m.reactions_haha ?? 0),
      exclamation: Number(m.reactions?.exclamation ?? m.reactions_exclamation ?? 0),
      question: Number(m.reactions?.question ?? m.reactions_question ?? 0),
    },
    myReaction: (m.my_reaction ?? m.myReaction ?? null) as any,
  } as ChatMessage;
}

export async function listConversations(): Promise<ChatConversation[]> {
  const res = await api.get('/internal-chat/conversations');
  const data = res?.data?.data || [];
  return (Array.isArray(data) ? data : []).map((c: any) => ({ id: String(c.id), type: c.type, name: c.name || 'Rozmowa', lastActivityAt: c.lastActivityAt, unreadCount: Number(c.unreadCount || 0), otherUserId: c.otherUserId || null }));
}

export async function getOrCreateDirect(otherUserId: string): Promise<ChatConversation> {
  const res = await api.post(`/internal-chat/conversations/direct/${encodeURIComponent(otherUserId)}`);
  const c = res?.data?.data;
  return { id: String(c.id), type: c.type || 'direct', name: c.name || 'Rozmowa prywatna' };
}

export async function listMessages(conversationId: string, pageToken?: string): Promise<ListMessagesResult> {
  const params: any = {};
  if (pageToken) params.before_id = pageToken;
  const res = await api.get(`/internal-chat/conversations/${encodeURIComponent(conversationId)}/messages`, { params });
  const payload = res?.data?.data || { items: [], nextBeforeId: null, readUpToByOthers: null };
  const items: ChatMessage[] = (payload.items || []).map(mapApiItemToChatMessage);
  const nextPageToken = payload.nextBeforeId ? String(payload.nextBeforeId) : null;
  return { items, nextPageToken, readUpToByOthers: payload.readUpToByOthers ? String(payload.readUpToByOthers) : null };
}

export async function listMessagesAfter(conversationId: string, afterId: string): Promise<ChatMessage[]> {
  const params: any = { after_id: afterId };
  const res = await api.get(`/internal-chat/conversations/${encodeURIComponent(conversationId)}/messages`, { params });
  const payload = res?.data?.data || { items: [] };
  return (payload.items || []).map(mapApiItemToChatMessage);
}

export async function sendMessage(conversationId: string, text: string): Promise<boolean> {
  const res = await api.post(`/internal-chat/conversations/${encodeURIComponent(conversationId)}/messages`, { content: text });
  return !!res?.data?.success;
}

/** Zapis odpowiedzi w tym samym wątku co @gemma (nadawca = INTERNAL_CHAT_GEMMA_BOT_USER_ID). Przy 503 — brak bota w .env, odpowiedź tylko lokalnie w UI. */
export async function postGemmaAssistantMessage(
  conversationId: string,
  content: string
): Promise<{ persisted: boolean; message: ChatMessage | null }> {
  try {
    const res = await api.post(`/internal-chat/conversations/${encodeURIComponent(conversationId)}/gemma-message`, { content });
    const ok = !!res?.data?.success;
    const raw = res?.data?.data?.message;
    const message = ok && raw ? mapApiItemToChatMessage(raw) : null;
    return { persisted: ok, message };
  } catch (e: any) {
    if (e?.response?.status === 503) return { persisted: false, message: null };
    throw e;
  }
}

export async function markRead(conversationId: string, lastReadMessageId?: string): Promise<void> {
  await api.post(`/internal-chat/conversations/${encodeURIComponent(conversationId)}/read`, lastReadMessageId ? { last_read_message_id: lastReadMessageId } : {});
}

export async function uploadImage(conversationId: string, file: File): Promise<{ ok: boolean; url?: string }>{
  const form = new FormData();
  form.append('image', file);
  const res = await api.post(`/internal-chat/conversations/${encodeURIComponent(conversationId)}/upload-image`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return { ok: !!res?.data?.success, url: res?.data?.data?.image_url };
}

export async function uploadFile(conversationId: string, file: File): Promise<{ ok: boolean; url?: string; fileName?: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post(`/internal-chat/conversations/${encodeURIComponent(conversationId)}/upload-file`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return { ok: !!res?.data?.success, url: res?.data?.data?.file_url, fileName: res?.data?.data?.original_filename };
}

export type ReactionType = 'up'|'down'|'smile'|'heart'|'haha'|'exclamation'|'question';
export async function reactMessage(messageId: string, reaction: ReactionType): Promise<void> {
  await api.post(`/internal-chat/messages/${encodeURIComponent(messageId)}/react`, { reaction });
}

export async function unreactMessage(messageId: string): Promise<void> {
  await api.delete(`/internal-chat/messages/${encodeURIComponent(messageId)}/react`);
}

export async function editMessage(messageId: string, content: string): Promise<boolean> {
  const res = await api.patch(`/internal-chat/messages/${encodeURIComponent(messageId)}`, { content });
  return !!res?.data?.success;
}

export interface ChatChannelMember {
  user_id: string;
  display_name?: string;
  email?: string;
  avatar_url?: string | null;
  joined_at?: string;
}

export async function createGroupChannel(title: string, memberIds: string[]): Promise<ChatConversation> {
  const res = await api.post('/internal-chat/conversations/group', { title, memberIds });
  const c = res?.data?.data;
  return { id: String(c.id), type: 'group', name: c.name || title };
}

export async function updateGroupChannel(conversationId: string, title?: string): Promise<void> {
  await api.put(`/internal-chat/conversations/${encodeURIComponent(conversationId)}`, { title });
}

export async function listChannelMembers(conversationId: string): Promise<ChatChannelMember[]> {
  const res = await api.get(`/internal-chat/conversations/${encodeURIComponent(conversationId)}/members`);
  return res?.data?.data || [];
}

export async function updateChannelMembers(conversationId: string, memberIds: string[]): Promise<void> {
  await api.put(`/internal-chat/conversations/${encodeURIComponent(conversationId)}/members`, { memberIds });
}

export async function deleteGroupChannel(conversationId: string): Promise<void> {
  await api.delete(`/internal-chat/conversations/${encodeURIComponent(conversationId)}`);
}


